"use client";

import React, { createContext, useContext, useMemo, useCallback, useEffect, useState, useRef } from "react";
import { useList, useGetIdentity } from "@refinedev/core";
import { graphQLService } from "../../services/graphql-service";
import { amplifySubscriptionManager, ConnectionState } from "../../services/amplify-subscriptions";
import { generateVirtualInstances, isUnmarkedLessonStatus } from "../../utils/virtualLessons";
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, parseISO } from "date-fns";
import { loggers } from "../../utils/logger";
import { calculateLessonFee, type LessonFeeResult } from "../../utils/lessonFee";
import { apiService } from "../../services/api";

// Types for the data entities
interface Student {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  status?: string;
  subject?: string;
  familyId?: string;
  [key: string]: any;
}

interface Location {
  id: string;
  name: string;
  address?: string;
  isDefault?: boolean;
  hourlyRate?: number;
  cancellationPolicy?: any;
  availabilityRules?: any[];
  [key: string]: any;
}

interface LessonSeries {
  id: string;
  studentId: string;
  locationId?: string;
  dayOfWeek: string;
  dayOfWeekNum: number;
  time: string;
  duration: number;
  startDate: string;
  endDate?: string;
  status?: string;
  subject?: string;
  [key: string]: any;
}

interface Lesson {
  id: string;
  studentId: string;
  locationId?: string;
  date: string;
  time: string;
  duration: number;
  status: string;
  subject?: string;
  seriesId?: string;
  recurring?: boolean;
  isVirtual?: boolean;
  [key: string]: any;
}

interface Family {
  id: string;
  familyName: string;
  [key: string]: any;
}

// Predefined calendar event (school terms, holidays, etc.)
interface PredefinedCalendarEvent {
  id: string;
  title: string;
  startDate: string;  // YYYY-MM-DD
  endDate: string;    // YYYY-MM-DD
  [key: string]: any;
}

// Predefined calendar (e.g., NSW School Year 2025, Public Holidays)
interface PredefinedCalendar {
  id: string;
  type: string;  // 'school_year', 'public_holidays', etc.
  name: string;
  year?: number;
  isCurrent?: boolean;
  events: PredefinedCalendarEvent[];
  [key: string]: any;
}

// Location calendar reference (links location to predefined calendar)
interface LocationCalendarRef {
  calendarType: string;
  availability_effect: 'available' | 'unavailable';
  isActive?: boolean;
  priority?: number;
}

// Unavailable event info (returned when date is unavailable for a location)
export interface UnavailableEventInfo {
  eventTitle: string;
  eventStart: string;
  eventEnd: string;
  calendarName: string;
  calendarType: string;
}

// Week cache for calendar data
interface WeekCache {
  weekKey: string;
  series: LessonSeries[];
  lessons: Lesson[];
  loadedAt: number;
}

// Options for getLessonsWithVirtualForDateRange
export interface LessonQueryOptions {
  includeCompleted?: boolean;  // Default: true - include completed lessons
  includeCancelled?: boolean;  // Default: true - include cancelled lessons
  onlyScheduled?: boolean;     // Default: false - if true, only return scheduled (for marking view)
  locationId?: string | null;  // Filter by location
  groupByDate?: boolean;       // Default: false - if true, returns Map<date, lessons[]>
}

// Context value type
interface DataManagerContextValue {
  // Raw data arrays
  students: Student[];
  locations: Location[];
  lessonSeries: LessonSeries[];
  families: Family[];
  predefinedCalendars: PredefinedCalendar[];
  
  // Lessons data (with week-based caching)
  currentWeekLessons: Lesson[];
  getLessonsForDate: (date: string) => Lesson[];
  getLessonsForDateRange: (startDate: string, endDate: string) => Lesson[];
  getLessonsWithVirtual: (date: Date) => Lesson[];
  
  // Date range queries with virtual lessons (for Dashboard components)
  getLessonsWithVirtualForDateRange: (
    startDate: Date,
    endDate: Date,
    options?: LessonQueryOptions
  ) => Promise<Map<string, Lesson[]>>;
  
  // Month-based query (for calendar dots)
  getMonthLessons: (month: Date, options?: LessonQueryOptions) => Promise<Map<string, Lesson[]>>;
  
  // Loading states
  isLoading: boolean;
  isStudentsLoading: boolean;
  isLocationsLoading: boolean;
  isSeriesLoading: boolean;
  isLessonsLoading: boolean;
  
  // Subscription connection state
  connectionState: ConnectionState;
  
  // Lookup helpers - memoized for performance
  getStudentById: (id: string | undefined | null) => Student | undefined;
  getLocationById: (id: string | undefined | null) => Location | undefined;
  getSeriesById: (id: string | undefined | null) => LessonSeries | undefined;
  getFamilyById: (id: string | undefined | null) => Family | undefined;
  
  // Name formatters
  getStudentName: (studentId: string | undefined | null) => string;
  getLocationName: (locationId: string | undefined | null) => string;
  
  // Fee calculation helper - uses location fallback if lesson doesn't have custom fee
  getLessonFee: (lesson: any) => { lessonFee: number; hourlyRate: number; feeSource: string; calculation: string };
  
  // Location availability helpers - check if date is unavailable for a location based on its calendars
  isDateUnavailableForLocation: (dateString: string, locationId: string | undefined | null) => boolean;
  getUnavailableEventForLocation: (dateString: string, locationId: string | undefined | null) => UnavailableEventInfo | null;
  
  // Current week management
  currentWeek: Date;
  setCurrentWeek: (date: Date) => void;
  
  // Refetch functions
  refetchStudents: () => void;
  refetchLocations: () => void;
  refetchSeries: () => void;
  refetchLessons: () => void;
  refetchAll: () => void;
  
  // Force refresh from server (bypasses cache)
  forceRefreshLessons: (silent?: boolean) => Promise<void>;
  
  // Optimistic update - add series without full refresh
  addSeriesOptimistically: (series: LessonSeries) => void;
  
  // Optimistic update - update series status without full refresh
  updateSeriesStatusOptimistically: (seriesId: string, status: string) => void;
  
  // Data version - increments when subscription updates are received
  // Components can watch this to trigger re-fetches of their local data
  dataVersion: number;
  
  // Last updated lesson from subscription - for targeted updates without full refetch
  lastLessonUpdate: Lesson | null;
  
  // Notify other components of a lesson update (for components that make direct API calls)
  // This allows CalendarSidebar to notify TodaysLessons when it marks a lesson as present/cancelled
  notifyLessonUpdate: (lesson: any) => void;
}

// Create context with undefined default
const DataManagerContext = createContext<DataManagerContextValue | undefined>(undefined);

// Helper to get week key
const getWeekKey = (date: Date): string => {
  const start = startOfWeek(date, { weekStartsOn: 1 });
  return format(start, 'yyyy-MM-dd');
};

// Provider component
export const DataManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get current user identity for subscriptions
  const { data: identity } = useGetIdentity<{ id: string; username?: string }>();
  const tutorId = identity?.id;
  
  // Current week for calendar views
  const [currentWeek, setCurrentWeek] = useState<Date>(new Date());
  
  // Week cache for lessons
  const [weekCache, setWeekCache] = useState<Map<string, WeekCache>>(new Map());
  const [isLessonsLoading, setIsLessonsLoading] = useState(false);
  
  // Real-time subscription connection state
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  
  // Data version counter - increments when subscription updates arrive
  // Components can watch this to know when to re-fetch their local data
  const [dataVersion, setDataVersion] = useState(0);
  
  // Last updated lesson from subscription - for targeted updates
  const [lastLessonUpdate, setLastLessonUpdate] = useState<Lesson | null>(null);
  
  // Track if subscriptions have been initialized
  const subscriptionsInitialized = useRef(false);
  
  // Debounce timer for subscription-triggered refreshes
  const seriesRefreshDebounceRef = useRef<NodeJS.Timeout | null>(null);
  
  // Predefined calendars (loaded once on mount)
  const [predefinedCalendars, setPredefinedCalendars] = useState<PredefinedCalendar[]>([]);
  const [isPredefinedCalendarsLoading, setIsPredefinedCalendarsLoading] = useState(true);
  const predefinedCalendarsLoaded = useRef(false);
  
  // Fetch all students via REST (Refine)
  const { 
    data: studentsData, 
    isLoading: isStudentsLoading,
    refetch: refetchStudents 
  } = useList<Student>({
    resource: "students",
    pagination: { mode: "off" },
    queryOptions: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
    },
  });

  // Fetch all locations via REST (Refine)
  const { 
    data: locationsData, 
    isLoading: isLocationsLoading,
    refetch: refetchLocations 
  } = useList<Location>({
    resource: "locations",
    pagination: { mode: "off" },
    queryOptions: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
    },
  });

  // Fetch all lesson series via REST (Refine)
  const { 
    data: seriesData, 
    isLoading: isSeriesLoading,
    refetch: refetchSeries 
  } = useList<LessonSeries>({
    resource: "lesson-series",
    pagination: { mode: "off" },
    queryOptions: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
    },
  });

  // Fetch all families via REST (Refine)
  const { 
    data: familiesData, 
    isLoading: isFamiliesLoading,
    refetch: refetchFamilies 
  } = useList<Family>({
    resource: "families",
    pagination: { mode: "off" },
    queryOptions: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
    },
  });

  // Extract arrays with fallback to empty arrays
  const students = useMemo(() => studentsData?.data || [], [studentsData]);
  const locations = useMemo(() => locationsData?.data || [], [locationsData]);
  const lessonSeries = useMemo(() => seriesData?.data || [], [seriesData]);
  const families = useMemo(() => familiesData?.data || [], [familiesData]);

  // Load predefined calendars once on mount
  useEffect(() => {
    if (predefinedCalendarsLoaded.current) return;
    
    const loadPredefinedCalendars = async () => {
      try {
        setIsPredefinedCalendarsLoading(true);
        const response = await apiService.getPredefinedCalendars() as any;
        
        if (response.success && response.data?.calendars) {
          // Filter to only current year calendars and normalize structure
          const calendars: PredefinedCalendar[] = response.data.calendars
            .filter((cal: any) => cal.isCurrent && cal.outdated !== true)
            .map((cal: any) => ({
              id: cal.id,
              type: cal.type,
              calendarType: cal.calendarType || cal.type,
              name: cal.name,
              year: cal.year,
              isCurrent: cal.isCurrent,
              events: (cal.events || []).map((event: any) => ({
                id: event.id,
                title: event.title || event.name,
                startDate: event.startDate || event.start,
                endDate: event.endDate || event.end,
                ...event,
              })),
            }));
          
          setPredefinedCalendars(calendars);
          loggers.dataManager.log(`Loaded ${calendars.length} predefined calendars with ${calendars.reduce((sum, c) => sum + c.events.length, 0)} total events`);
          console.log('📅 [DataManager] Predefined calendars loaded:', calendars.map(c => ({ type: c.type, name: c.name, events: c.events.length })));
          // Only mark as loaded when calendars are successfully loaded
          predefinedCalendarsLoaded.current = true;
        } else {
          loggers.dataManager.warn('Predefined calendars API returned unsuccessful response, will retry on next mount');
        }
      } catch (error) {
        loggers.dataManager.error('Error loading predefined calendars:', error);
      } finally {
        setIsPredefinedCalendarsLoading(false);
      }
    };
    
    loadPredefinedCalendars();
  }, []);

  // Create lookup maps for O(1) access
  const studentsMap = useMemo(() => {
    const map = new Map<string, Student>();
    students.forEach(student => map.set(student.id, student));
    return map;
  }, [students]);

  const locationsMap = useMemo(() => {
    const map = new Map<string, Location>();
    locations.forEach(location => map.set(location.id, location));
    return map;
  }, [locations]);

  const seriesMap = useMemo(() => {
    const map = new Map<string, LessonSeries>();
    lessonSeries.forEach(series => map.set(series.id, series));
    return map;
  }, [lessonSeries]);

  const familiesMap = useMemo(() => {
    const map = new Map<string, Family>();
    families.forEach(family => map.set(family.id, family));
    return map;
  }, [families]);

  // Map of calendarType → one calendar whose events cover every loaded year.
  const predefinedCalendarsMap = useMemo(() => {
    const map = new Map<string, PredefinedCalendar>();
    predefinedCalendars.forEach(calendar => {
      const key = (calendar as any).calendarType || calendar.type;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, { ...calendar, events: [...calendar.events] });
        return;
      }
      existing.events = [...existing.events, ...calendar.events];
    });
    return map;
  }, [predefinedCalendars]);

  // Lookup helpers
  const getStudentById = useCallback(
    (id: string | undefined | null) => id ? studentsMap.get(id) : undefined,
    [studentsMap]
  );

  const getLocationById = useCallback(
    (id: string | undefined | null) => id ? locationsMap.get(id) : undefined,
    [locationsMap]
  );

  const getSeriesById = useCallback(
    (id: string | undefined | null) => id ? seriesMap.get(id) : undefined,
    [seriesMap]
  );

  const getFamilyById = useCallback(
    (id: string | undefined | null) => id ? familiesMap.get(id) : undefined,
    [familiesMap]
  );

  // Name formatters
  const getStudentName = useCallback(
    (studentId: string | undefined | null): string => {
      if (!studentId) return "Unknown Student";
      const student = studentsMap.get(studentId);
      if (!student) return "Unknown Student";
      const name = `${student.firstName || ''} ${student.lastName || ''}`.trim();
      return name || "Unknown Student";
    },
    [studentsMap]
  );

  const getLocationName = useCallback(
    (locationId: string | undefined | null): string => {
      if (!locationId) return "";
      const location = locationsMap.get(locationId);
      return location?.name || "";
    },
    [locationsMap]
  );

  // Calculate lesson fee with automatic location fallback
  const getLessonFee = useCallback(
    (lesson: any): LessonFeeResult => {
      const location = lesson?.locationId ? locationsMap.get(lesson.locationId) : undefined;
      return calculateLessonFee(lesson, location);
    },
    [locationsMap]
  );

  // Check if a date is unavailable for a location based on its calendars
  // Returns true if the date falls within any "unavailable" period
  const isDateUnavailableForLocation = useCallback(
    (dateString: string, locationId: string | undefined | null): boolean => {
      // No locationId means no location-specific restrictions (online, student home, etc.)
      if (!locationId) return false;
      
      const location = locationsMap.get(locationId);
      // If locationId exists but location not found, treat as available
      // The virtual lesson will be shown with a warning about missing location
      if (!location) return false;
      
      // Get the location's calendars (LocationCalendarRef[])
      const locationCalendars = (location as any).calendars as LocationCalendarRef[] | undefined;
      if (!locationCalendars || locationCalendars.length === 0) return false;
      
      // Check each calendar attached to the location
      for (const locCal of locationCalendars) {
        // Skip inactive calendars
        if (locCal.isActive === false) continue;
        
        // Get the predefined calendar by type
        const predefinedCal = predefinedCalendarsMap.get(locCal.calendarType);
        if (!predefinedCal) continue;
        
        // Check if any event in this calendar covers the date
        for (const event of predefinedCal.events) {
          if (event.type === 'regional_holiday') continue;
          const eventStart = event.startDate;
          const eventEnd = event.endDate;
          
          // Check if date falls within event range
          if (dateString >= eventStart && dateString <= eventEnd) {
            // If this calendar marks dates as unavailable, the date is unavailable
            if (locCal.availability_effect === 'unavailable') {
              return true;
            }
          }
        }
      }
      
      return false;
    },
    [locationsMap, predefinedCalendarsMap]
  );

  // Get details about why a date is unavailable for a location
  // Returns the event info (title, dates, calendar name) or null if available
  const getUnavailableEventForLocation = useCallback(
    (dateString: string, locationId: string | undefined | null): UnavailableEventInfo | null => {
      if (!locationId) return null;
      
      const location = locationsMap.get(locationId);
      if (!location) return null;
      
      const locationCalendars = (location as any).calendars as LocationCalendarRef[] | undefined;
      
      // Debug logging
      console.log(`📅 [getUnavailableEvent] ${dateString}: Location "${location.name}" calendars:`, locationCalendars);
      
      if (!locationCalendars || locationCalendars.length === 0) return null;
      
      for (const locCal of locationCalendars) {
        console.log(`📅 [getUnavailableEvent] ${dateString}: Checking calendar type="${locCal.calendarType}", effect="${locCal.availability_effect}"`);
        
        if (locCal.isActive === false) continue;
        
        const predefinedCal = predefinedCalendarsMap.get(locCal.calendarType);
        if (!predefinedCal) {
          console.log(`📅 [getUnavailableEvent] ${dateString}: Predefined calendar not found for "${locCal.calendarType}"`);
          continue;
        }
        
        console.log(`📅 [getUnavailableEvent] ${dateString}: Found predefined calendar "${predefinedCal.name}" with ${predefinedCal.events.length} events`);
        
        for (const event of predefinedCal.events) {
          if (event.type === 'regional_holiday') continue;
          if (dateString >= event.startDate && dateString <= event.endDate) {
            console.log(`📅 [getUnavailableEvent] ${dateString}: Date matches event "${event.title}" (${event.startDate} to ${event.endDate}), effect=${locCal.availability_effect}`);
            if (locCal.availability_effect === 'unavailable') {
              return {
                eventTitle: event.title,
                eventStart: event.startDate,
                eventEnd: event.endDate,
                calendarName: predefinedCal.name,
                calendarType: predefinedCal.type,
              };
            }
          }
        }
      }
      
      return null;
    },
    [locationsMap, predefinedCalendarsMap]
  );

  // Load lessons for a specific week
  const loadWeekData = useCallback(async (week: Date): Promise<WeekCache> => {
    const weekKey = getWeekKey(week);
    const start = startOfWeek(week, { weekStartsOn: 1 });
    const end = endOfWeek(week, { weekStartsOn: 1 });
    const startDate = format(start, 'yyyy-MM-dd');
    const endDate = format(end, 'yyyy-MM-dd');

    loggers.dataManager.log(`Loading week ${weekKey}: ${startDate} to ${endDate}`);

    try {
      // Load lesson series active during this week.
      // listActiveLessonSeries filters by startDate <= date AND endDate >= date
      // (day-of-week matching is client-side). Querying all 7 days used to multiply
      // identical network calls ~7x for typical term-length series. Sampling the
      // week's start AND end covers series that begin or end mid-week.
      // Rare edge case still missed: a series whose entire active window is a
      // strict mid-week sub-range (start > Monday AND end < Sunday) — uncommon for
      // real tutor schedules (series usually span a term).
      const allSeriesResults = await Promise.all(
        [start, end].map(day =>
          graphQLService.listActiveLessonSeries(format(day, 'yyyy-MM-dd'), false)
        )
      );
      
      // Deduplicate series
      const seriesMap = new Map();
      allSeriesResults.flat().forEach(s => {
        if (!seriesMap.has(s.id)) {
          seriesMap.set(s.id, s);
        }
      });
      const series = Array.from(seriesMap.values());

      // Load real lesson instances for this week
      const lessons = await graphQLService.listLessons({
        startDate,
        endDate,
      }, false);

      const cache: WeekCache = {
        weekKey,
        series,
        lessons,
        loadedAt: Date.now(),
      };

      loggers.dataManager.success(`Loaded week ${weekKey}: ${series.length} series, ${lessons.length} lessons`);
      return cache;
    } catch (error) {
      loggers.dataManager.error(`Error loading week ${weekKey}:`, error);
      throw error;
    }
  }, []);

  // Load required weeks (current + 2 before + 2 after)
  const loadRequiredWeeks = useCallback(async () => {
    setIsLessonsLoading(true);
    
    try {
      const requiredWeeks: Date[] = [];
      for (let i = -2; i <= 2; i++) {
        const week = i < 0 ? subWeeks(currentWeek, Math.abs(i)) : addWeeks(currentWeek, i);
        requiredWeeks.push(week);
      }

      const newCache = new Map(weekCache);
      const loadPromises: Promise<void>[] = [];

      for (const week of requiredWeeks) {
        const weekKey = getWeekKey(week);
        const cached = newCache.get(weekKey);
        const cacheAge = cached ? Date.now() - cached.loadedAt : Infinity;
        const shouldReload = !cached || cacheAge > 5 * 60 * 1000; // 5 min cache

        if (shouldReload) {
          loadPromises.push(
            loadWeekData(week).then(cache => {
              newCache.set(weekKey, cache);
            })
          );
        }
      }

      await Promise.all(loadPromises);

      // Remove weeks that are no longer required
      const requiredKeys = new Set(requiredWeeks.map(getWeekKey));
      for (const key of newCache.keys()) {
        if (!requiredKeys.has(key)) {
          newCache.delete(key);
        }
      }

      setWeekCache(newCache);
    } catch (error) {
      loggers.dataManager.error('Error loading weeks:', error);
    } finally {
      setIsLessonsLoading(false);
    }
  }, [currentWeek, loadWeekData]);

  // Load weeks when currentWeek changes
  useEffect(() => {
    loadRequiredWeeks();
  }, [loadRequiredWeeks]);

  // Get lessons for current week
  const currentWeekLessons = useMemo(() => {
    const weekKey = getWeekKey(currentWeek);
    const cached = weekCache.get(weekKey);
    return cached?.lessons || [];
  }, [currentWeek, weekCache]);

  // Get lessons for a specific date
  const getLessonsForDate = useCallback((date: string): Lesson[] => {
    for (const cache of weekCache.values()) {
      const lessons = cache.lessons.filter(l => l.date === date);
      if (lessons.length > 0) return lessons;
    }
    return [];
  }, [weekCache]);

  // Get lessons for a date range
  const getLessonsForDateRange = useCallback((startDate: string, endDate: string): Lesson[] => {
    const lessons: Lesson[] = [];
    for (const cache of weekCache.values()) {
      const matching = cache.lessons.filter(l => l.date >= startDate && l.date <= endDate);
      lessons.push(...matching);
    }
    // Deduplicate by ID
    const uniqueLessons = Array.from(new Map(lessons.map(l => [l.id, l])).values());
    return uniqueLessons.sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`));
  }, [weekCache]);

  // Get lessons with virtual instances for a specific date
  const getLessonsWithVirtual = useCallback((date: Date): Lesson[] => {
    const weekKey = getWeekKey(date);
    const cached = weekCache.get(weekKey);

    if (!cached) return [];

    const dateString = format(date, 'yyyy-MM-dd');
    // Pass isDateUnavailableForLocation to filter out virtual lessons during unavailable periods
    const virtualInstances = generateVirtualInstances(cached.series, date, cached.lessons, isDateUnavailableForLocation);
    const realLessons = cached.lessons.filter(l => l.date === dateString);

    // Merge virtual and real, with real overriding virtual
    const lessonMap = new Map<string, Lesson>();

    // Add virtual first, with location warning if location is missing
    virtualInstances.forEach(v => {
      const key = `${v.studentId}-${v.time}-${v.date}`;
      const location = v.locationId ? locationsMap.get(v.locationId) : undefined;
      const hasMissingLocation = v.locationId && !location;
      lessonMap.set(key, { 
        ...v, 
        isVirtual: true,
        locationWarning: hasMissingLocation ? 'Location not found - please update series' : undefined,
      });
    });

    // Override with real. Several lessons can share a student/time/date after a
    // cancel-and-rebook; a later cancelled row must not hide the scheduled one.
    realLessons.forEach(r => {
      const key = `${r.studentId}-${r.time}-${r.date}`;
      const existing = lessonMap.get(key);
      if (existing && !existing.isVirtual && existing.status !== 'cancelled' && r.status === 'cancelled') {
        return;
      }
      lessonMap.set(key, { ...r, isVirtual: false });
    });

    // Enrich with student/location data
    return Array.from(lessonMap.values()).map(lesson => ({
      ...lesson,
      student: getStudentById(lesson.studentId),
      location: getLocationById(lesson.locationId),
    })).sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  }, [weekCache, getStudentById, getLocationById, isDateUnavailableForLocation, locationsMap]);

  // Get lessons with virtual instances for a date range
  // This is the unified function for Dashboard components
  const getLessonsWithVirtualForDateRange = useCallback(async (
    startDate: Date,
    endDate: Date,
    options: LessonQueryOptions = {}
  ): Promise<Map<string, Lesson[]>> => {
    const {
      includeCompleted = true,
      includeCancelled = true,
      onlyScheduled = false,
      locationId = null,
    } = options;

    const startDateStr = format(startDate, 'yyyy-MM-dd');
    const endDateStr = format(endDate, 'yyyy-MM-dd');

    loggers.dataManager.log(`getLessonsWithVirtualForDateRange: ${startDateStr} to ${endDateStr}`);

    try {
      // Prefer the already-loaded series list. Querying listActiveLessonSeries every
      // 7 days across a year can be 30+ parallel AppSync calls — one failure used to
      // empty the whole past-roll ("All caught up!") while the calendar still worked.
      let series: LessonSeries[] = lessonSeries.filter((item) => {
        if (item.status === 'cancelled') return false;
        if (item.startDate && item.startDate > endDateStr) return false;
        if (item.endDate && item.endDate < startDateStr) return false;
        return true;
      });

      if (series.length === 0) {
        const sampled = await Promise.allSettled([
          graphQLService.listActiveLessonSeries(startDateStr, false),
          graphQLService.listActiveLessonSeries(endDateStr, false),
        ]);
        const seriesMap = new Map<string, LessonSeries>();
        sampled.forEach((result) => {
          if (result.status !== 'fulfilled') return;
          result.value.forEach((item) => {
            if (!seriesMap.has(item.id)) seriesMap.set(item.id, item);
          });
        });
        series = Array.from(seriesMap.values());
      }

      const realLessons = await graphQLService.listLessons({
        startDate: startDateStr,
        endDate: endDateStr,
        limit: 1000,
      }, false);

      const resultMap = new Map<string, Lesson[]>();
      let currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const endLocal = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

      while (currentDate <= endLocal) {
        const dateStr = format(currentDate, 'yyyy-MM-dd');
        const virtualInstances = generateVirtualInstances(
          series,
          currentDate,
          realLessons,
          isDateUnavailableForLocation
        );
        const realForDate = realLessons.filter(l => l.date === dateStr);
        const lessonMap = new Map<string, Lesson>();

        virtualInstances.forEach(v => {
          const key = `${v.studentId}-${v.time}-${v.date}`;
          const location = v.locationId ? locationsMap.get(v.locationId) : undefined;
          const hasMissingLocation = v.locationId && !location;
          lessonMap.set(key, {
            ...v,
            isVirtual: true,
            locationWarning: hasMissingLocation ? 'Location not found - please update series' : undefined,
          });
        });

        realForDate.forEach(r => {
          const key = `${r.studentId}-${r.time}-${r.date}`;
          const existing = lessonMap.get(key);
          if (existing && !existing.isVirtual && existing.status !== 'cancelled' && r.status === 'cancelled') {
            return;
          }
          lessonMap.set(key, { ...r, isVirtual: false });
        });

        let lessons = Array.from(lessonMap.values())
          .map(lesson => ({
            ...lesson,
            student: getStudentById(lesson.studentId),
            location: getLocationById(lesson.locationId),
          }))
          .sort((a, b) => (a.time || '').localeCompare(b.time || ''));

        if (onlyScheduled) {
          lessons = lessons.filter(l => isUnmarkedLessonStatus(l.status));
        } else {
          if (!includeCompleted) {
            lessons = lessons.filter(l => l.status !== 'completed');
          }
          if (!includeCancelled) {
            lessons = lessons.filter(l => l.status !== 'cancelled');
          }
        }

        if (locationId) {
          lessons = lessons.filter(l => l.locationId === locationId);
        }

        if (lessons.length > 0) {
          resultMap.set(dateStr, lessons);
        }

        currentDate = addDays(currentDate, 1);
      }

      loggers.dataManager.log(`getLessonsWithVirtualForDateRange: returned ${resultMap.size} dates with lessons`);
      return resultMap;
    } catch (error) {
      loggers.dataManager.error('Error in getLessonsWithVirtualForDateRange:', error);
      return new Map();
    }
  }, [isDateUnavailableForLocation, locationsMap, getStudentById, getLocationById, lessonSeries]);

  // Get lessons for a month (optimized for calendar dots)
  const getMonthLessons = useCallback(async (
    month: Date,
    options: LessonQueryOptions = {}
  ): Promise<Map<string, Lesson[]>> => {
    const startOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
    const endOfMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    
    return getLessonsWithVirtualForDateRange(startOfMonth, endOfMonth, options);
  }, [getLessonsWithVirtualForDateRange]);

  // Patch a lesson into weekCache in-place. Prefer this over clearing the cache —
  // wiping weekCache without an immediate reload leaves the calendar empty with no
  // loading indicator (silent blank) until the next navigation/refetch.
  const patchLessonInWeekCache = useCallback((lesson: any) => {
    if (!lesson?.id) return;

    setWeekCache(prev => {
      const newCache = new Map(prev);
      let changed = false;

      for (const [weekKey, cache] of newCache) {
        const existingIndex = cache.lessons.findIndex(l => l.id === lesson.id);
        if (existingIndex >= 0) {
          const newLessons = [...cache.lessons];
          newLessons[existingIndex] = { ...newLessons[existingIndex], ...lesson };
          newCache.set(weekKey, { ...cache, lessons: newLessons, loadedAt: Date.now() });
          changed = true;
          continue;
        }

        // Newly created real instance (e.g. marking a virtual lesson) — insert into
        // the week that contains its date so virtual→real swap is instant.
        if (lesson.date) {
          const weekEnd = format(addDays(parseISO(weekKey), 6), 'yyyy-MM-dd');
          if (lesson.date >= weekKey && lesson.date <= weekEnd) {
            newCache.set(weekKey, {
              ...cache,
              lessons: [...cache.lessons, lesson],
              loadedAt: Date.now(),
            });
            changed = true;
          }
        }
      }

      return changed ? newCache : prev;
    });
  }, []);

  // Force refresh lessons from server.
  // silent=true keeps the existing board mounted (no full-page Spin) — used after
  // drag-reschedule / other in-place mutations where flashing the spinner is worse
  // than a brief stale→fresh content swap.
  const forceRefreshLessons = useCallback(async (silent = false) => {
    if (!silent) setIsLessonsLoading(true);
    graphQLService.clearCache(); // Clear GraphQL service cache
    
    try {
      const weekKey = getWeekKey(currentWeek);
      const cache = await loadWeekData(currentWeek);
      
      setWeekCache(prev => {
        const newCache = new Map(prev);
        newCache.set(weekKey, cache);
        return newCache;
      });
    } finally {
      if (!silent) setIsLessonsLoading(false);
    }
  }, [currentWeek, loadWeekData]);

  // Notify other components of a lesson update (called by components that make direct API calls)
  // This allows CalendarSidebar to notify TodaysLessons when it marks a lesson as present/cancelled
  const notifyLessonUpdate = useCallback((lesson: any) => {
    loggers.dataManager.log('notifyLessonUpdate called for lesson:', lesson.id, lesson.status);
    setLastLessonUpdate(lesson);
    setDataVersion(v => v + 1);
    patchLessonInWeekCache(lesson);
  }, [patchLessonInWeekCache]);

  // Refetch lessons (uses cache)
  const refetchLessons = useCallback(() => {
    loadRequiredWeeks();
  }, [loadRequiredWeeks]);

  // Optimistic update - add series to cache without full refresh
  // This provides instant UI feedback when creating a new lesson series
  const addSeriesOptimistically = useCallback((series: LessonSeries) => {
    loggers.dataManager.log('Adding series optimistically:', series.id);
    
    // Add to all relevant week caches
    setWeekCache(prev => {
      const newCache = new Map(prev);
      
      // Add series to all cached weeks (it may span multiple weeks)
      for (const [weekKey, cache] of newCache) {
        // Check if series already exists in this cache
        const existingIndex = cache.series.findIndex(s => s.id === series.id);
        if (existingIndex === -1) {
          // Add new series
          newCache.set(weekKey, {
            ...cache,
            series: [...cache.series, series],
            loadedAt: Date.now(), // Update timestamp
          });
        } else {
          // Update existing series
          const newSeries = [...cache.series];
          newSeries[existingIndex] = series;
          newCache.set(weekKey, {
            ...cache,
            series: newSeries,
            loadedAt: Date.now(),
          });
        }
      }
      
      return newCache;
    });
    
    // Increment data version to trigger UI re-render
    setDataVersion(v => v + 1);
  }, []);

  // Optimistic update - update series status in cache without full refresh
  // This provides instant UI feedback when cancelling a series
  const updateSeriesStatusOptimistically = useCallback((seriesId: string, status: string) => {
    loggers.dataManager.log('Updating series status optimistically:', seriesId, status);
    
    setWeekCache(prev => {
      const newCache = new Map(prev);
      
      for (const [weekKey, cache] of newCache) {
        const existingIndex = cache.series.findIndex(s => s.id === seriesId);
        if (existingIndex >= 0) {
          const newSeries = [...cache.series];
          newSeries[existingIndex] = { ...newSeries[existingIndex], status };
          newCache.set(weekKey, {
            ...cache,
            series: newSeries,
            loadedAt: Date.now(),
          });
        }
      }
      
      return newCache;
    });
    
    // Increment data version to trigger UI re-render
    setDataVersion(v => v + 1);
  }, []);

  // Refetch all data
  const refetchAll = useCallback(() => {
    refetchStudents();
    refetchLocations();
    refetchSeries();
    refetchFamilies();
    refetchLessons();
  }, [refetchStudents, refetchLocations, refetchSeries, refetchFamilies, refetchLessons]);

  // Initialize real-time subscriptions using AWS Amplify
  useEffect(() => {
    if (!tutorId || subscriptionsInitialized.current) {
      return;
    }

    loggers.subscriptions.log('Initializing Amplify subscriptions for tutor:', tutorId);
    subscriptionsInitialized.current = true;

    // Set up subscription callbacks to trigger data refresh when updates arrive
    amplifySubscriptionManager.setCallbacks({
      onLessonUpdate: (lesson) => {
        loggers.subscriptions.log('Lesson update received:', lesson.id, lesson.status);
        // Set the last updated lesson for targeted updates (no full refetch needed)
        setLastLessonUpdate(lesson);
        // Increment data version so components know to check for updates
        setDataVersion(v => v + 1);
        // Patch in-place — clearing weekCache here used to blank the calendar
        // with no spinner until the next navigation triggered a reload.
        patchLessonInWeekCache(lesson);
      },
      onLessonSeriesUpdate: (series) => {
        loggers.subscriptions.log('Series update received:', series.id);
        // Debounce the refresh to prevent rapid consecutive calls
        // (e.g., when manual refresh() and subscription both trigger)
        if (seriesRefreshDebounceRef.current) {
          clearTimeout(seriesRefreshDebounceRef.current);
        }
        seriesRefreshDebounceRef.current = setTimeout(() => {
          loggers.subscriptions.log('Series refresh executing (debounced):', series.id);
          // Instead of clearing cache, just update the series in the cache
          // This preserves optimistic updates and prevents UI flicker
          setWeekCache(prev => {
            const newCache = new Map(prev);
            for (const [weekKey, cache] of newCache) {
              const existingIndex = cache.series.findIndex(s => s.id === series.id);
              if (existingIndex >= 0) {
                // Update existing series
                const newSeries = [...cache.series];
                newSeries[existingIndex] = series;
                newCache.set(weekKey, { ...cache, series: newSeries });
              } else {
                // Add new series
                newCache.set(weekKey, { ...cache, series: [...cache.series, series] });
              }
            }
            return newCache;
          });
          // Also update the REST-fetched series list
          refetchSeries();
          // Increment data version so components know to refresh
          setDataVersion(v => v + 1);
        }, 500); // 500ms debounce
      },
      onStudentUpdate: (student) => {
        loggers.subscriptions.log('Student update received:', student.id);
        refetchStudents();
        setDataVersion(v => v + 1);
      },
      onLocationUpdate: (location) => {
        loggers.subscriptions.log('Location update received:', location.id);
        refetchLocations();
        setDataVersion(v => v + 1);
      },
      onFamilyUpdate: (family) => {
        loggers.subscriptions.log('Family update received:', family.id);
        refetchFamilies();
        setDataVersion(v => v + 1);
      },
      onInvoiceUpdate: (invoice) => {
        loggers.subscriptions.log('Invoice update received:', invoice.id);
        // Invoices aren't cached in DataManager, but components can listen for this
        setDataVersion(v => v + 1);
      },
      onConnectionStateChange: (state) => {
        loggers.subscriptions.log('Connection state changed:', state);
        setConnectionState(state);
      },
      onError: (error) => {
        loggers.subscriptions.error('Subscription error:', error);
      },
    });

    // Initialize the Amplify subscription connection
    amplifySubscriptionManager.initialize(tutorId);

    // Cleanup on unmount
    return () => {
      loggers.subscriptions.log('Cleaning up subscriptions');
      amplifySubscriptionManager.disconnect();
      subscriptionsInitialized.current = false;
    };
  }, [tutorId, refetchLessons, refetchSeries, refetchStudents, refetchLocations, refetchFamilies, patchLessonInWeekCache]);

  // Combined loading state
  const isLoading = isStudentsLoading || isLocationsLoading || isSeriesLoading || isFamiliesLoading;

  // Context value - memoized to prevent unnecessary re-renders
  const contextValue = useMemo<DataManagerContextValue>(() => ({
    // Data arrays
    students,
    locations,
    lessonSeries,
    families,
    predefinedCalendars,
    
    // Lessons
    currentWeekLessons,
    getLessonsForDate,
    getLessonsForDateRange,
    getLessonsWithVirtual,
    getLessonsWithVirtualForDateRange,
    getMonthLessons,

    // Loading states
    isLoading,
    isStudentsLoading,
    isLocationsLoading,
    isSeriesLoading,
    isLessonsLoading,
    
    // Connection state
    connectionState,
    
    // Lookup helpers
    getStudentById,
    getLocationById,
    getSeriesById,
    getFamilyById,
    
    // Name formatters
    getStudentName,
    getLocationName,
    
    // Fee calculation
    getLessonFee,
    
    // Location availability
    isDateUnavailableForLocation,
    getUnavailableEventForLocation,

    // Week management
    currentWeek,
    setCurrentWeek,
    
    // Refetch functions
    refetchStudents,
    refetchLocations,
    refetchSeries,
    refetchLessons,
    refetchAll,
    forceRefreshLessons,
    addSeriesOptimistically,
    updateSeriesStatusOptimistically,
    
    // Data version for subscription-triggered updates
    dataVersion,
    
    // Last updated lesson for targeted updates
    lastLessonUpdate,
    
    // Manual notification for components that make direct API calls
    notifyLessonUpdate,
  }), [
    students,
    locations,
    lessonSeries,
    families,
    predefinedCalendars,
    currentWeekLessons,
    getLessonsForDate,
    getLessonsForDateRange,
    getLessonsWithVirtual,
    getLessonsWithVirtualForDateRange,
    getMonthLessons,
    isLoading,
    isStudentsLoading,
    isLocationsLoading,
    isSeriesLoading,
    isLessonsLoading,
    connectionState,
    getStudentById,
    getLocationById,
    getSeriesById,
    getFamilyById,
    getStudentName,
    getLocationName,
    getLessonFee,
    isDateUnavailableForLocation,
    getUnavailableEventForLocation,
    currentWeek,
    setCurrentWeek,
    refetchStudents,
    refetchLocations,
    refetchSeries,
    refetchLessons,
    refetchAll,
    forceRefreshLessons,
    addSeriesOptimistically,
    updateSeriesStatusOptimistically,
    dataVersion,
    lastLessonUpdate,
    notifyLessonUpdate,
  ]);

  return (
    <DataManagerContext.Provider value={contextValue}>
      {children}
    </DataManagerContext.Provider>
  );
};

// Custom hook to use the data manager
export const useDataManager = (): DataManagerContextValue => {
  const context = useContext(DataManagerContext);
  if (!context) {
    throw new Error("useDataManager must be used within a DataManagerProvider");
  }
  return context;
};

// Export context for advanced use cases
export { DataManagerContext };

// Re-export lesson fee utilities for standalone use
export { calculateLessonFee, calculateCancellationFee, getLessonFeeWithLocationLookup } from '../../utils/lessonFee';
export type { LessonFeeResult, LessonForFeeCalculation, LocationForFeeCalculation } from '../../utils/lessonFee';
